import React from 'react';
import './Triangles.css';

export type TriSize = 's' | 'm' | 'l';
export type TriDir = 'up' | 'right' | 'down' | 'left';

/** Height = 0.866 x side: every glyph is equilateral. */
export const EQ = Math.sqrt(3) / 2;

/**
 * Visma's three glyph sizes against the cap height of the text they sit in: small 1/3, medium
 * 1/2, big 2/3 (2 : 3 : 4; measured from Visma Text, cap 690 = 230 / 345 / 460). In ems for Inter,
 * whose cap height is 0.727em.
 */
export const INTER_CAP = 0.727;
export const TRI_SIDE_EM: Record<TriSize, number> = {
  s: INTER_CAP / 3,
  m: INTER_CAP / 2,
  l: (INTER_CAP * 2) / 3,
};

const POINTS: Record<TriDir, string> = {
  right: `0,0 ${EQ * 100},50 0,100`,
  left: `${EQ * 100},0 0,50 ${EQ * 100},100`,
  up: `0,${EQ * 100} 50,0 100,${EQ * 100}`,
  down: `0,0 100,0 50,${EQ * 100}`,
};

/** The outline variant, shrunk about the centroid so its 1px stroke stays inside the glyph. */
function inset(points: string, k = 0.8): string {
  const pts = points.split(' ').map((p) => p.split(',').map(Number));
  const cx = (pts[0][0] + pts[1][0] + pts[2][0]) / 3;
  const cy = (pts[0][1] + pts[1][1] + pts[2][1]) / 3;
  return pts.map(([x, y]) => `${(cx + (x - cx) * k).toFixed(2)},${(cy + (y - cy) * k).toFixed(2)}`).join(' ');
}

/**
 * The marker glyph (DESIGN_SYSTEM.md "Triangles in Grow"): an inline, equilateral triangle sized
 * from the adjacent text and centred on half its cap height, in currentColor. Use it only where
 * it means "you are here" or "next". Decorative: always aria-hidden.
 */
export function TriGlyph({ size = 'm', dir = 'right', outline = false, className }: {
  size?: TriSize; dir?: TriDir;
  /** A step not reached yet: the same triangle as a 1px outline inside its edges. */
  outline?: boolean;
  className?: string;
}) {
  const across = dir === 'right' || dir === 'left';
  return (
    <svg
      className={['ui-tri', `ui-tri--${size}`, across ? 'ui-tri--across' : 'ui-tri--along', outline && 'ui-tri--outline', className].filter(Boolean).join(' ')}
      viewBox={across ? `0 0 ${EQ * 100} 100` : `0 0 100 ${EQ * 100}`}
      aria-hidden="true"
      focusable="false"
      data-testid="tri-glyph"
      data-dir={dir}
      data-outline={outline ? 'true' : undefined}
    >
      <polygon points={outline ? inset(POINTS[dir]) : POINTS[dir]} vectorEffect={outline ? 'non-scaling-stroke' : undefined} />
    </svg>
  );
}
